import { useEffect, useMemo, useState } from 'react';

import { App, Breadcrumb, Button, Card, Result, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Layout from 'components/Layout/Layout';

import { ApiErrorData } from 'models/ApiResponse';
import {
  OperationAssignment,
  OperationAssignmentContext,
  OperationRequest,
  OperationSupervision,
} from 'models/OperationRequest';

import { OperationRequirementService } from 'services/OperationRequirementService';

import AssignmentBoard from './components/AssignmentBoard';
import SupervisorStep from './components/SupervisorStep';
import { effectiveMap, missingStores } from './data/supervision';

import './AssignOperationRequest.scss';

const LIST_PATH = '/portal-go/requirements-review';

function AssignOperationRequest() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const i18nBase = 'scenes.requirements_operation.components.assign_people';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState<OperationAssignmentContext | null>(
    null
  );
  const [supervision, setSupervision] = useState<OperationSupervision>({
    sups: [],
    map: {},
  });
  const [editingSupervisors, setEditingSupervisors] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const response = await OperationRequirementService.getAssignmentContext(
        id
      );
      if (cancelled) return;
      setLoading(false);
      if (response.success) {
        const data = response.data as OperationAssignmentContext;
        const saved = data.supervision || { sups: [], map: {} };
        const complete =
          saved.sups.length > 0 &&
          missingStores(
            data.stores,
            saved.sups,
            effectiveMap(data.stores, saved.sups, saved.map)
          ).length === 0;
        setContext(data);
        setSupervision(saved);
        setEditingSupervisors(!complete);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.load`),
          3.5
        );
        setContext(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, message, t, i18nBase]);

  const supervisorNames = useMemo(
    () => (context?.supervisors || []).map((s) => s.label),
    [context]
  );

  const handleSaveSupervision = async (next: OperationSupervision) => {
    if (!context) return;
    setSaving(true);
    const response = await OperationRequirementService.setSupervision(
      context.request.id,
      next
    );
    setSaving(false);
    if (response.success) {
      setSupervision(next);
      setEditingSupervisors(false);
      message.success(
        t(`${i18nBase}.messages.supervision_saved`, {
          id: context.request.id,
        }),
        3.5
      );
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t(`${i18nBase}.errors.save`),
        3.5
      );
    }
  };

  const handleAssignmentSaved = (
    assignment: OperationAssignment,
    request?: OperationRequest
  ) => {
    setContext((prev) =>
      prev
        ? {
            ...prev,
            request: request?.id
              ? { ...prev.request, ...request }
              : prev.request,
            assignment,
          }
        : prev
    );
  };

  const requestId = context?.request.id || id;
  const stores = context?.stores || [];

  const renderContent = () => {
    if (loading) {
      return (
        <Card bordered className="assign-operation-request__card">
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      );
    }
    if (!context) {
      return (
        <Card bordered className="assign-operation-request__card">
          <Result
            status="warning"
            title={t(`${i18nBase}.errors.load`)}
            extra={
              <Button onClick={() => navigate(LIST_PATH)}>
                {t(`${i18nBase}.buttons.back`)}
              </Button>
            }
          />
        </Card>
      );
    }
    if (context.request.status === 'publicado') {
      return (
        <Card bordered className="assign-operation-request__card">
          <Result
            status="success"
            title={t(`${i18nBase}.published.title`, {
              id: context.request.id,
            })}
            subTitle={t(`${i18nBase}.published.description`)}
            extra={
              <Button type="primary" onClick={() => navigate(LIST_PATH)}>
                {t(`${i18nBase}.buttons.back`)}
              </Button>
            }
          />
        </Card>
      );
    }
    if (stores.length === 0) {
      return (
        <Card bordered className="assign-operation-request__card">
          <Result
            status="info"
            title={t(`${i18nBase}.empty_stores.title`)}
            subTitle={t(`${i18nBase}.empty_stores.description`)}
            extra={
              <Button
                onClick={() =>
                  navigate(`${LIST_PATH}/new?editId=${context.request.id}`)
                }
              >
                {t(`${i18nBase}.empty_stores.edit`)}
              </Button>
            }
          />
        </Card>
      );
    }
    if (editingSupervisors) {
      return (
        <SupervisorStep
          stores={stores}
          supervisors={supervisorNames}
          initial={supervision}
          saving={saving}
          onDone={handleSaveSupervision}
        />
      );
    }
    if (context.turnos.length === 0) {
      return (
        <Card bordered className="assign-operation-request__card">
          <Result
            status="info"
            title={t(`${i18nBase}.empty_turnos.title`)}
            subTitle={t(`${i18nBase}.empty_turnos.description`)}
            extra={
              <Button
                onClick={() =>
                  navigate(`${LIST_PATH}/new?editId=${context.request.id}`)
                }
              >
                {t(`${i18nBase}.empty_stores.edit`)}
              </Button>
            }
          />
        </Card>
      );
    }
    return (
      <AssignmentBoard
        context={context}
        supervision={supervision}
        onEditSupervisors={() => setEditingSupervisors(true)}
        onSaved={handleAssignmentSaved}
      />
    );
  };

  return (
    <Layout title="" breadcrumbMode="none">
      <div className="assign-operation-request">
        <Breadcrumb
          className="assign-operation-request__breadcrumb"
          separator="›"
          items={[
            { title: t(`${i18nBase}.breadcrumb.portal_go`) },
            {
              title: (
                <Link to={LIST_PATH}>
                  {t(`${i18nBase}.breadcrumb.requirements`)}
                </Link>
              ),
            },
            {
              title: t(`${i18nBase}.breadcrumb.current`, { id: requestId }),
            },
          ]}
        />

        <div className="assign-operation-request__header">
          <div>
            <h1 className="assign-operation-request__title">
              {t(`${i18nBase}.title`)}
            </h1>
            <p className="assign-operation-request__subtitle">
              {t(`${i18nBase}.subtitle`)}
            </p>
          </div>
          <Button onClick={() => navigate(LIST_PATH)}>
            {t(`${i18nBase}.buttons.back`)}
          </Button>
        </div>

        {renderContent()}
      </div>
    </Layout>
  );
}

export default AssignOperationRequest;
